import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { memo, useCallback, useContext, useEffect, useRef } from "react";
import { useSetupGuideOptional } from "../../provider/use-setup-guide";
import { isTaskComplete } from "../../registry/helpers";
import { getProductConfig } from "../../registry/product-registry";
import type { Product } from "../../registry/types";
import { shouldAutoCloseContextualModal } from "./should-auto-close-contextual-modal";
import { useContextualModalLifecycle } from "./use-contextual-modal-lifecycle";

export interface TaskContextualModalRendererProps {
  product: Product;
  /**
   * Called when the modal closes due to task completion (auto or manual).
   * Provider uses this to fire the task-completion event channel and
   * refetch any dependent state.
   */
  onTaskCompleted: (taskId: string) => void;
}

/**
 * Single config-driven modal. Reads `?setupTask=<id>` from URL, looks up
 * the task in the registered product config, and renders the task's
 * contextualModal config OR a custom renderBody component.
 *
 * Auto-closes itself when the task's predicate transitions false → true
 * (e.g. merchant connected Stripe on the integrations page) — the user
 * shouldn't have to manually close after they completed the action.
 *
 * Mounts once at app shell level so any page can show its task-specific
 * modal without per-page wiring.
 */
export const TaskContextualModalRenderer = memo(
  function TaskContextualModalRenderer({
    product,
    onTaskCompleted,
  }: TaskContextualModalRendererProps) {
    const { activeTaskId, clearActiveTaskId } = useContextualModalLifecycle();
    const setupGuide = useSetupGuideOptional();
    const team = useContext(TeamContext);
    const config = getProductConfig(product);

    const handleClose = useCallback(() => {
      clearActiveTaskId();
    }, [clearActiveTaskId]);

    const handleComplete = useCallback(() => {
      if (activeTaskId) onTaskCompleted(activeTaskId);
      clearActiveTaskId();
    }, [activeTaskId, onTaskCompleted, clearActiveTaskId]);

    // Track previous completion state to detect a false→true transition.
    const prevCompleteRef = useRef<boolean>(false);
    const task =
      config && activeTaskId ? config.tasks[activeTaskId] : undefined;
    const currentlyComplete =
      !!task && !!team && !!setupGuide?.activeProgress
        ? isTaskComplete(task, team, setupGuide.activeProgress)
        : false;

    useEffect(() => {
      if (
        shouldAutoCloseContextualModal({
          previousTaskComplete: prevCompleteRef.current,
          currentTaskComplete: currentlyComplete,
        })
      ) {
        handleComplete();
      }
      prevCompleteRef.current = currentlyComplete;
    }, [currentlyComplete, handleComplete]);

    if (!config || !activeTaskId) return null;
    if (!task?.contextualModal) return null;

    const modal = task.contextualModal;
    const RenderBody = modal.renderBody;

    return (
      <RedoModal
        isOpen
        modalSize="md"
        onModalCloseRequested={handleClose}
        primaryButton={
          RenderBody
            ? undefined
            : { text: modal.ctaText, onClick: handleComplete }
        }
        secondaryButton={
          RenderBody ? undefined : { text: "Not now", onClick: handleClose }
        }
        subtitle={modal.body}
        title={modal.title}
      >
        {RenderBody ? (
          <RenderBody onClose={handleClose} onComplete={handleComplete} />
        ) : null}
      </RedoModal>
    );
  },
);
